import { motion } from "framer-motion";
import Button from "@/components/site/Button";
import useSeo from "@/hooks/useSeo";
import { breadcrumbLd } from "@/lib/jsonld";
import { usePageContent, ContentLoading } from "@/lib/content";

const EASE = [0.16, 1, 0.3, 1];

// Terugval-teksten: alleen gebruikt als de content-API faalt of te traag is.
const DEFAULTS = {
    "methodes.seo-title": "Mijn methodes | Human Design Studio",
    "methodes.seo-description":
        "Vier methodes die Marcel van der Veen inzet in een sessie: Human Design, NLP, familieopstellingen en lichaamswerk. Lees wanneer welke methode past.",
    "methodes.hero.eyebrow": "Methodes",
    "methodes.hero.h1-a": "Vier gereedschappen,",
    "methodes.hero.h1-accent": "één vraag",
    "methodes.hero.intro":
        "In een sessie gebruik ik Human Design als kaart. Maar ik wil niet dat je alleen informatie krijgt. Ik help je echt contact maken met je talenten en je uitdagingen. Daarvoor gebruik ik ook NLP, familieopstellingen en lichaamswerk. Zo ga je het niet alleen begrijpen, maar ook voelen en toepassen.",
    "methodes.wanneer-label": "Wanneer inzetten",
    "methodes.human-design.naam": "Human Design",
    "methodes.human-design.oneliner": "Je blauwdruk, hoe je van nature werkt.",
    "methodes.human-design.body": [
        "Human Design is de kaart waarop je precies kunt aflezen hoe jij in elkaar zit. Uit je geboortedatum, geboortetijd en geboorteplaats komt je bodygraph: je type, je autoriteit, je profiel, je centra en je kanalen.",
        "In een sessie gebruik ik je bodygraph als vertrekpunt. Zo krijg je taal en beeld bij wat je diep vanbinnen al aanvoelt: waar jouw energie soepel loopt, en waar hij vastloopt als je jezelf forceert.",
    ],
    "methodes.human-design.wanneer":
        "Als startpunt van elk traject en als spiegel bij grote keuzes over werk, relaties, ouderschap of richting.",
    "methodes.nlp.naam": "NLP",
    "methodes.nlp.oneliner": "Je denken en je gewoontes.",
    "methodes.nlp.body": [
        "NLP (Neuro-Linguistisch Programmeren) kijkt naar de manier waarop je denkt, praat en reageert. Welke overtuigingen sturen je gedrag? Welke patronen herhalen zich, ook al wil je het anders?",
        "Ik werk als NLP Practitioner, Master en Trainer met technieken die snel effect hebben: hoe je een blokkade ombuigt naar beweging, hoe je een gewoonte vervangt door iets wat wel bij je past.",
    ],
    "methodes.nlp.wanneer":
        "Als een patroon in je denken of doen je vastzet en je concreet wilt veranderen wat je morgen anders doet.",
    "methodes.familieopstellingen.naam": "Familieopstellingen",
    "methodes.familieopstellingen.oneliner": "De onderstroom zichtbaar maken.",
    "methodes.familieopstellingen.body": [
        "In elke familie werken oude patronen door: loyaliteiten, gemis, uitgesproken en onuitgesproken verhalen. Familieopstellingen maken die onderstroom zichtbaar. Wat lijkt van jou, blijkt vaak van het systeem.",
        "Ik werk zowel individueel als in kleine groepen. Soms is een korte opstelling genoeg om iets te verhelderen dat jaren onhelder bleef.",
    ],
    "methodes.familieopstellingen.wanneer":
        "Bij thema's die generaties raken: verlies, plek innemen, verbinding met ouders of kinderen, terugkerende relatiepatronen.",
    "methodes.lichaamswerk.naam": "Lichaamswerk",
    "methodes.lichaamswerk.oneliner": "Van denken naar voelen.",
    "methodes.lichaamswerk.body": [
        "Je lichaam weet meer dan je hoofd. In een driejarige opleiding leerde ik hoe je via ademhaling, beweging en aandacht toegang krijgt tot wat het lichaam vasthoudt.",
        "In een sessie kan lichaamswerk klein zijn: even landen, ademen, voelen waar spanning zit. Of iets uitgebreider: gerichte oefeningen die iets in beweging brengen wat met woorden niet lukt.",
    ],
    "methodes.lichaamswerk.wanneer":
        "Wanneer je alles al hebt bedacht en alsnog vastloopt. Of als je merkt dat je vooral in je hoofd zit en het lichaam iets probeert te zeggen.",
    "methodes.cta.eyebrow": "Volgende stap",
    "methodes.cta.heading": "Klaar om ermee aan de slag te gaan?",
    "methodes.cta.text":
        "In één sessie combineer ik wat op dat moment past. Human Design als kaart, en de andere gereedschappen als je het echt wilt voelen en toepassen.",
    "methodes.cta.boek": "Boek een sessie",
    "methodes.cta.qa": "Gratis Q&A",
};

// Volgorde, nummering en accentkleuren zijn structuur en blijven in de code
const METHODE_IDS = ["human-design", "nlp", "familieopstellingen", "lichaamswerk"];
const METHODE_META = [
    { eyebrow: "01", accent: "clay" },
    { eyebrow: "02", accent: "green" },
    { eyebrow: "03", accent: "clay" },
    { eyebrow: "04", accent: "green" },
];

export default function Methodes() {
    const c = usePageContent("methodes", DEFAULTS);
    useSeo({
        title: c ? c.t("methodes.seo-title") : DEFAULTS["methodes.seo-title"],
        description: c
            ? c.t("methodes.seo-description")
            : DEFAULTS["methodes.seo-description"],
        path: "/methodes",
        jsonLd: [breadcrumbLd("Methodes", "/methodes")],
    });

    if (!c) return <ContentLoading />;

    const methodes = METHODE_IDS.map((id, i) => ({
        id,
        eyebrow: METHODE_META[i].eyebrow,
        accent: METHODE_META[i].accent,
        naam: c.t(`methodes.${id}.naam`),
        oneliner: c.t(`methodes.${id}.oneliner`),
        body: c.list(`methodes.${id}.body`),
        wanneer: c.t(`methodes.${id}.wanneer`),
    }));

    return (
        <>
            {/* HERO */}
            <section
                data-testid="page-methodes"
                className="mx-auto max-w-[1440px] px-5 md:px-10 pt-24 md:pt-40 pb-16 md:pb-24"
            >
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-8"
                >
                    {c.t("methodes.hero.eyebrow")}
                </motion.p>
                <div className="overflow-hidden">
                    <motion.h1
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{ duration: 1, ease: EASE }}
                        className="font-[Fraunces] text-[13vw] md:text-[9vw] lg:text-[96px] leading-[0.98] tracking-[-0.02em] text-[var(--hds-ink)] max-w-[20ch]"
                        style={{ fontWeight: 300 }}
                    >
                        {c.t("methodes.hero.h1-a")}{" "}
                        <em
                            className="italic text-[var(--hds-clay)]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("methodes.hero.h1-accent")}
                        </em>
                        .
                    </motion.h1>
                </div>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
                    className="mt-8 md:mt-10 max-w-[62ch] text-[17px] md:text-[19px] leading-[1.6] text-[var(--hds-ink)]/82"
                >
                    {c.t("methodes.hero.intro")}
                </motion.p>
            </section>

            {/* METHODES DETAIL */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 pb-24 md:pb-32">
                <div className="border-t border-[var(--hds-line)]">
                    {methodes.map((m, i) => (
                        <motion.article
                            key={m.id}
                            id={m.id}
                            data-testid={`methode-${m.id}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10%" }}
                            transition={{ duration: 0.9, ease: EASE, delay: 0.05 * i }}
                            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-12 md:py-16 border-b border-[var(--hds-line)]"
                        >
                            <div className="md:col-span-3">
                                <span
                                    className={`font-[Fraunces] text-[36px] md:text-[52px] leading-none ${
                                        m.accent === "green"
                                            ? "text-[var(--hds-green)]"
                                            : "text-[var(--hds-clay)]"
                                    }`}
                                    style={{ fontWeight: 300, fontStyle: "italic" }}
                                >
                                    {m.eyebrow}
                                </span>
                                <h2
                                    className="mt-3 font-[Fraunces] text-[30px] md:text-[38px] leading-[1.05] tracking-[-0.01em] text-[var(--hds-ink)]"
                                    style={{ fontWeight: 300 }}
                                >
                                    {m.naam}
                                </h2>
                                <p className="mt-2 text-[14px] tracking-wide text-[var(--hds-ink)]/60">
                                    {m.oneliner}
                                </p>
                            </div>
                            <div className="md:col-span-6 space-y-4 text-[16px] md:text-[17px] leading-[1.65] text-[var(--hds-ink)]/82">
                                {m.body.map((p, k) => (
                                    <p key={k}>{p}</p>
                                ))}
                            </div>
                            <div className="md:col-span-3">
                                <p className="text-[10.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-3">
                                    {c.t("methodes.wanneer-label")}
                                </p>
                                <p className="text-[15px] leading-[1.6] text-[var(--hds-ink)]/82">
                                    {m.wanneer}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section
                data-testid="methodes-cta"
                className="relative bg-[var(--hds-clay)] text-[var(--hds-card)]"
            >
                <div className="mx-auto max-w-[1440px] px-5 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <p className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-card)]/70 mb-6">
                            {c.t("methodes.cta.eyebrow")}
                        </p>
                        <h2
                            className="font-[Fraunces] text-[8vw] md:text-[5vw] lg:text-[60px] leading-[1.05] tracking-[-0.02em]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("methodes.cta.heading")}
                        </h2>
                        <p className="mt-6 max-w-[52ch] text-[16.5px] leading-[1.6] text-[var(--hds-card)]/80">
                            {c.t("methodes.cta.text")}
                        </p>
                    </div>
                    <div className="md:col-span-4 flex flex-col sm:flex-row md:justify-end gap-3">
                        <Button
                            to="/boeken"
                            data-testid="methodes-cta-boeken"
                            variant="onClayPrimary"
                            icon="arrow"
                        >
                            {c.t("methodes.cta.boek")}
                        </Button>
                        <Button
                            to="/gratis-qa"
                            data-testid="methodes-cta-qa"
                            variant="onClaySecondary"
                        >
                            {c.t("methodes.cta.qa")}
                        </Button>
                    </div>
                </div>
            </section>
        </>
    );
}
